/**
 * Open from, or save to, OneDrive and SharePoint.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { GraphError, oneDrive, type DriveItem, type DriveLocation, type Site } from '../files/oneDrive'
import { formatSize } from './CompressDialog'
import { isConvertibleName, isOfficeName } from '../files/createPdf'

type Place = 'mine' | 'recent' | 'shared' | 'sites'

interface Props {
  mode: 'open' | 'save'
  /** Suggested file name when saving. */
  filename?: string
  onOpen?: (item: DriveItem) => void
  /** Also offer Office files and pictures, which open as a PDF made from them. */
  convertible?: boolean
  onSave?: (folder: DriveLocation, name: string, overwrite: boolean) => Promise<boolean>
  onClose: () => void
}

export function OneDriveDialog({ mode, filename = 'Document.pdf', onOpen, onSave, onClose, convertible = false }: Props) {
  const opens = (item: DriveItem) => item.isPdf || (convertible && isConvertibleName(item.name))
  const [place, setPlace] = useState<Place>('mine')
  // Folders drilled into, root first. Sites add a site and library level.
  const [trail, setTrail] = useState<DriveLocation[]>([])
  const [items, setItems] = useState<DriveItem[]>([])
  const [sites, setSites] = useState<Site[] | null>(null)
  const [libraries, setLibraries] = useState<{ site: Site; list: DriveLocation[] } | null>(null)
  const [query, setQuery] = useState('')
  const [name, setName] = useState(filename)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [chosen, setChosen] = useState<string | null>(null)

  // Each listing request gets a number; only the latest may show its result.
  // Without this, a slow "My files" listing arriving after the user had
  // switched to Recent replaced the list under the cursor, and a click opened
  // a different file.
  const latest = useRef(0)

  const guard = useCallback(async (work: (current: () => boolean) => Promise<void>) => {
    const ticket = ++latest.current
    const current = () => ticket === latest.current
    setBusy(true)
    setError('')
    try {
      await work(current)
    } catch (err) {
      if (!current()) return
      setError(err instanceof GraphError && err.status === 403
        ? 'Odie has not been given access to OneDrive yet, or you do not have access here. ' + err.message
        : err instanceof Error ? err.message : String(err))
    } finally {
      if (current()) setBusy(false)
    }
  }, [])

  const openFolder = useCallback((location: DriveLocation, newTrail: DriveLocation[]) =>
    guard(async (current) => {
      const listed = await oneDrive.folder(location)
      if (!current()) return
      setItems(listed)
      setTrail(newTrail)
      setChosen(null)
    }), [guard])

  const go = useCallback((next: Place) => {
    setPlace(next)
    setQuery('')
    setItems([])
    setTrail([])
    setLibraries(null)
    setSites(null)
    setChosen(null)
    void guard(async (current) => {
      if (next === 'mine') {
        const root = await oneDrive.myDrive()
        const listed = await oneDrive.folder(root)
        if (!current()) return
        setTrail([root])
        setItems(listed)
      } else if (next === 'recent') {
        const listed = await oneDrive.recent()
        if (current()) setItems(listed)
      } else if (next === 'shared') {
        const listed = await oneDrive.shared()
        if (current()) setItems(listed)
      } else {
        const listed = await oneDrive.sites()
        if (current()) setSites(listed)
      }
    })
  }, [guard])

  useEffect(() => { go('mine') }, [go])

  const here = trail[trail.length - 1] ?? null
  const search = () => guard(async (current) => {
    if (place === 'sites') {
      const listed = await oneDrive.sites(query)
      if (!current()) return
      setLibraries(null)
      setTrail([])
      setSites(listed)
    } else if (here && query.trim()) {
      const listed = await oneDrive.search(here, query.trim())
      if (current()) setItems(listed)
    } else if (here) {
      const listed = await oneDrive.folder(here)
      if (current()) setItems(listed)
    }
  })

  const activate = (item: DriveItem) => {
    if (item.folder) {
      void openFolder({ driveId: item.driveId, itemId: item.id, name: item.name }, [...trail, {
        driveId: item.driveId, itemId: item.id, name: item.name }])
    } else if (mode === 'open' && opens(item)) {
      onOpen?.(item)
    } else if (mode === 'save' && item.isPdf) {
      setName(item.name)
      setChosen(item.id)
    }
  }

  const save = () => guard(async () => {
    if (!here || !onSave) return
    let fileName = name.trim()
    if (!fileName) return
    if (!/\.pdf$/i.test(fileName)) fileName += '.pdf'
    const exists = items.some((i) => !i.folder && i.name.toLowerCase() === fileName.toLowerCase())
    if (exists && !window.confirm(`${fileName} already exists in ${here.name}. Replace it?`)) return
    if (await onSave(here, fileName, exists)) onClose()
  })

  const shown = items.filter((i) => i.folder || opens(i) || mode === 'save')
  const inSiteList = place === 'sites' && !libraries && trail.length === 0
  const inLibraryList = place === 'sites' && libraries && trail.length === 0
  const canSaveHere = mode === 'save' && Boolean(here) && place !== 'recent'

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="od-title">
      <div className="modal__panel od">
        <h2 className="modal__title" id="od-title">
          {mode === 'open' ? 'Open from OneDrive or SharePoint' : 'Save to OneDrive or SharePoint'}
        </h2>
        <div className="od__body">
          <nav className="od__places">
            {([['mine', 'My files'], ['recent', 'Recent'], ['shared', 'Shared with me'], ['sites', 'SharePoint sites']] as const)
              .filter(([id]) => mode === 'open' || id !== 'recent')
              .map(([id, label]) => (
                <button key={id} className="od__place" data-active={place === id || undefined}
                        onClick={() => go(id)}>{label}</button>
              ))}
          </nav>
          <div className="od__main">
            <div className="od__bar">
              <div className="od__crumbs">
                {place === 'sites' && (
                  <button className="od__crumb" onClick={() => go('sites')}>Sites</button>
                )}
                {place === 'sites' && libraries && (
                  <button className="od__crumb" onClick={() => { setTrail([]); setItems([]) }}>
                    {libraries.site.name}
                  </button>
                )}
                {trail.map((loc, i) => (
                  <button key={`${loc.driveId}:${loc.itemId}`} className="od__crumb"
                          onClick={() => void openFolder(loc, trail.slice(0, i + 1))}>{loc.name}</button>
                ))}
              </div>
              {place !== 'recent' && place !== 'shared' && (
                <form className="od__search" onSubmit={(e) => { e.preventDefault(); void search() }}>
                  <input value={query} placeholder={place === 'sites' ? 'Find a site' : 'Search here'}
                         onChange={(e) => setQuery(e.target.value)} />
                </form>
              )}
            </div>

            <div className="od__list" role="listbox" aria-busy={busy}>
              {inSiteList && (sites ?? []).map((site) => (
                <button key={site.id} className="od__item" data-folder
                        onClick={() => void guard(async (current) => {
                          const list = await oneDrive.libraries(site)
                          if (current()) setLibraries({ site, list })
                        })}>
                  <span className="od__icon">{'\u{1F3E2}'}</span>
                  <span className="od__name">{site.name}</span>
                  <span className="od__meta">{site.url.replace(/^https:\/\//, '')}</span>
                </button>
              ))}
              {inLibraryList && libraries!.list.map((library) => (
                <button key={library.driveId} className="od__item" data-folder
                        onClick={() => void openFolder(library, [library])}>
                  <span className="od__icon">{'\u{1F4DA}'}</span>
                  <span className="od__name">{library.name}</span>
                </button>
              ))}
              {!inSiteList && !inLibraryList && shown.map((item) => (
                <button
                  key={`${item.driveId}:${item.id}`}
                  className="od__item"
                  data-folder={item.folder || undefined}
                  data-selected={chosen === item.id || undefined}
                  disabled={mode === 'save' && !item.folder && !item.isPdf}
                  onClick={() => (item.folder || mode === 'save') ? activate(item) : setChosen(item.id)}
                  onDoubleClick={() => activate(item)}
                >
                  <span className="od__icon">
                    {item.folder ? '\u{1F4C1}' : item.isPdf ? '\u{1F4C4}' : isOfficeName(item.name) ? '\u{1F4DD}' : '\u{1F5BC}'}
                  </span>
                  <span className="od__name">{item.name}</span>
                  {!item.folder && !item.isPdf && mode === 'open' && (
                    <span className="od__meta">opens as a PDF</span>
                  )}
                  <span className="od__meta">
                    {item.folder ? '' : formatSize(item.size)}
                    {item.modified ? ` · ${new Date(item.modified).toLocaleDateString()}` : ''}
                    {item.modifiedBy ? ` · ${item.modifiedBy}` : ''}
                  </span>
                </button>
              ))}
              {!busy && !inSiteList && !inLibraryList && shown.length === 0 && (
                <p className="od__empty">{query ? 'Nothing found.' : convertible ? 'No PDFs, Office files, pictures or folders here.' : 'No PDFs or folders here.'}</p>
              )}
              {!busy && inSiteList && (sites ?? []).length === 0 && (
                <p className="od__empty">
                  {query ? 'No sites found.' : 'You do not follow any sites. Search for one above.'}
                </p>
              )}
              {busy && <p className="od__empty">Loading&hellip;</p>}
            </div>
            {error && <p className="modal__hint modal__hint--warn">{error}</p>}
          </div>
        </div>
        <div className="modal__footer">
          {mode === 'save' && (
            <label className="field od__filename">
              <span>File name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          )}
          <button className="btn" onClick={onClose}>Cancel</button>
          {mode === 'open' ? (
            <button className="btn btn--primary"
                    disabled={!chosen || busy}
                    onClick={() => {
                      const item = items.find((i) => i.id === chosen)
                      if (item) activate(item)
                    }}>Open</button>
          ) : (
            <button className="btn btn--primary" disabled={!canSaveHere || !name.trim() || busy}
                    title={canSaveHere ? undefined : 'Open a folder to save into'}
                    onClick={() => void save()}>Save here</button>
          )}
        </div>
      </div>
    </div>
  )
}
